/**
 * shared/herofx.ts —— 运营总览 Hero 的 Canvas 环境层 + 大数跳数（2026-10-07，用户拍板）
 *
 * 用户反馈 Hero「还是太素」，要求把原来的静态装饰（CSS 点阵 + CSS 漂移光晕 + SVG 星链）
 * 改成 JS 重绘、让它活起来。本模块把三层收进**一张画布**：
 *   ① 漂移光晕：沿用原 `ovHeroGlow` 的参数（navy-3 提亮、14s 往返），从 CSS 搬进画布；
 *   ② 15px 点阵：底图静止；一道斜向扫光每轮掠过一次；少量点缓慢闪烁；指针附近的点被照亮；
 *   ③ 星链：节点微幅漂移；光点沿连线流动、到站时节点闪一下；指针靠近时节点向指针连线。
 *   ④ 首次进页（`.page.mo-in`）：星链节点从左到右依次点亮、连线随之生长——只播一次。
 *   ⑤ **避让文字**（2026-10-07 用户要求「文字附近的连线淡一点」）：四格的标签 / 大数 / 说明行 / 环
 *      所在区域（外扩 14px 渐变带），星链的连线、节点、流光都压到 30%。点阵不避让——它是均匀底噪。
 * 另外导出 `countUp()`：首次进页时四个大数从 0 跳到真值，与四枚环的入场同节奏（340ms 起、1.1s）。
 *
 * 🔴 全部**纯装饰、不读任何业务数据**；颜色只有**白色透明度** + 光晕的 navy-3，
 *   快报海报色板、暖橙、达成金一个都不进来（金 = 已提交，画在装饰上就是语义污染）。
 * 🔴 这一整张画布在循环动画白名单里只占**一行**（替掉原 `ovHeroGlow` 那一行），总数仍是 7 处。
 * ⚠ 循环的启停不靠页面生命周期（页面契约没有 unmount）：每帧自检画布是否还在文档里；
 *   Hero 滚出视口时停帧（IntersectionObserver），回来再拉起。
 * ⚠ reduced-motion：`15_media.css` 的全局兜底管不到 requestAnimationFrame，这里自己判——
 *   开了就只画一帧静态（星链全亮、无扫光无流光），大数不跳。
 * ⚠ 画布取不到 2D 上下文时什么都不做：`.hero` 不挂 `.fx-on`，CSS 点阵（`.hero::before`）照常显示。
 * ⚠ 排序 / 打卡后重拉会**整页重画**、画布换新：状态留在本模块，`mountHeroFx` 只接回新画布，
 *   星链不重播点亮（只有 firstPaint 才挂 `.mo-in`）。
 */

interface Dot { x: number; y: number; tw: number; ph: number }
interface Node { u: number; v: number; r: number; hub: boolean; ph: number; sp: number; rank: number }
interface Edge { a: number; b: number }
interface Pulse { e: number; dir: 1 | -1; t: number; len: number; hops: number }
interface Box { l: number; t: number; r: number; b: number }

const TAU = Math.PI * 2;
const REDUCE = '(prefers-reduced-motion: reduce)';
/** Hero 竖排的断点，与 `06_overview.css` / `22_overview_hero.css` 一致：竖排后不画星链 */
const NARROW = '(max-width:1250px)';
const GAP = 15;          // 点阵间距，与原 CSS 点阵同参数
const DOT_A = 0.09;      // 点阵底亮度（Hero 底 --navy 比快报封面浅，.14 会发脏）
const SWEEP_MS = 9000;   // 扫光一轮：前 55% 在掠过，其余时间停在画外
const INTRO_MS = 1500;

const mq = (q: string): boolean => typeof matchMedia === 'function' && matchMedia(q).matches;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const easeOut = (k: number): number => 1 - Math.pow(1 - clamp(k, 0, 1), 3);

/** 固定种子的伪随机：节点布局每次进页都一样，不会"这次好看下次难看" */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* 星链：在 Hero 右侧 72% 的区域里按「抖动网格」撒 7 列 × 3 行节点，每个节点连最近的两个邻居。
   坐标存成 0—1 的相对值（u 横 / v 竖），任何宽度下构图一致。 */
const NET = (() => {
  const r = rng(20261007);
  const nodes: Node[] = [];
  const COLS = 7, ROWS = 3;
  for (let c = 0; c < COLS; c++) {
    for (let w = 0; w < ROWS; w++) {
      if (r() < 0.14) continue; // 挖掉几个，避免读成整齐的格子
      nodes.push({
        u: (c + 0.5 + (r() - 0.5) * 0.8) / COLS,
        v: (w + 0.5 + (r() - 0.5) * 0.75) / ROWS,
        r: 1.9 + r() * 0.9,
        hub: false,
        ph: r() * TAU,
        sp: 0.55 + r() * 0.5,
        rank: 0,
      });
    }
  }
  // 三枚枢纽：稍大稍亮（沿用原 SVG「三枚枢纽」的构图）
  for (const i of [3, Math.floor(nodes.length / 2), nodes.length - 4]) {
    const n = nodes[i];
    if (n) { n.hub = true; n.r = 3.1; }
  }
  // 点亮顺序：从左到右（阅读方向），与扫光同向
  nodes
    .map((n, i) => ({ i, u: n.u }))
    .sort((a, b) => a.u - b.u)
    .forEach((o, k) => { const n = nodes[o.i]; if (n) n.rank = k; });
  const edges: Edge[] = [];
  const seen = new Set<string>();
  nodes.forEach((n, i) => {
    nodes
      .map((m, j) => ({ j, d: Math.hypot((m.u - n.u) * 2.6, m.v - n.v) })) // 横向拉伸后再比距离：区域是扁的
      .filter((o) => o.j !== i)
      .sort((a, b) => a.d - b.d)
      .slice(0, 2)
      .forEach(({ j }) => {
        const k = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (!seen.has(k)) { seen.add(k); edges.push({ a: Math.min(i, j), b: Math.max(i, j) }); }
      });
  });
  return { nodes, edges };
})();

const S = {
  hero: null as HTMLElement | null,
  cv: null as HTMLCanvasElement | null,
  base: null as HTMLCanvasElement | null, // 点阵底图（离屏缓存，尺寸变了才重画）
  baseKey: '',
  dots: [] as Dot[],
  W: 0,
  H: 0,
  running: false,
  visible: true,
  t0: 0,
  intro0: -1, // 点亮开场的起点；-1 = 本次不播（直接全亮）
  px: null as number | null,
  py: 0,
  pulses: [] as Pulse[],
  nextPulse: 0,
  flash: new Map<number, number>(), // 节点 → 最近一次被光点"到站"的时间
  text: [] as Box[], // 需要避让的文字 / 环区域（相对 Hero 左上角）
  textAt: 0,         // 上次量文字的时间：字体晚到会让行宽变化，所以每秒重量一次
  io: null as IntersectionObserver | null,
  ro: null as ResizeObserver | null,
};

/** 运营总览 `mounted()` 里调用：接上（新的）画布；`intro` = 本次是否首次进页 */
export function mountHeroFx(hero: HTMLElement | null, intro: boolean): void {
  if (!hero) return;
  const cv = hero.querySelector<HTMLCanvasElement>('.hero-fx');
  if (!cv?.getContext('2d')) return; // 画布不可用：保留 CSS 点阵兜底
  S.io?.disconnect();
  S.ro?.disconnect();
  S.hero = hero;
  S.cv = cv;
  S.px = null;
  hero.classList.add('fx-on');

  const still = mq(REDUCE);
  S.intro0 = intro && !still ? performance.now() : -1;
  if (intro) { S.pulses = []; S.flash.clear(); S.nextPulse = 0; }

  /* 指针：只在有鼠标的设备上响应；监听挂在 Hero 上（画布 pointer-events:none，不挡按钮） */
  if (!still && mq('(pointer:fine)')) {
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      S.px = e.clientX - r.left;
      S.py = e.clientY - r.top;
    }, { passive: true });
    hero.addEventListener('pointerleave', () => { S.px = null; });
  }

  if (typeof ResizeObserver === 'function') {
    S.ro = new ResizeObserver(() => { if (still) draw(performance.now(), true); });
    S.ro.observe(hero);
  }

  if (still) { draw(performance.now(), true); return; }

  if (typeof IntersectionObserver === 'function') {
    S.io = new IntersectionObserver((ents) => {
      S.visible = ents.some((x) => x.isIntersecting);
      if (S.visible) start();
    });
    S.io.observe(hero);
  }
  start();
}

function start(): void {
  if (S.running) return;
  S.running = true;
  if (!S.t0) S.t0 = performance.now();
  requestAnimationFrame(loop);
}

function loop(now: number): void {
  if (!S.cv?.isConnected || !S.visible) { S.running = false; return; }
  draw(now, false);
  requestAnimationFrame(loop);
}

/** 尺寸变了才重建：画布像素尺寸 + 点阵底图 */
function fit(): CanvasRenderingContext2D | null {
  const cv = S.cv, hero = S.hero;
  if (!cv || !hero) return null;
  const W = hero.clientWidth, H = hero.clientHeight;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.round(W * dpr), h = Math.round(H * dpr);
  if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
  S.W = W; S.H = H;
  const key = `${w}x${h}`;
  if (key !== S.baseKey) {
    S.baseKey = key;
    S.dots = [];
    const r = rng(7);
    const base = S.base ?? document.createElement('canvas');
    base.width = w; base.height = h;
    const b = base.getContext('2d');
    if (b) {
      b.setTransform(dpr, 0, 0, dpr, 0, 0);
      b.fillStyle = `rgba(255,255,255,${DOT_A})`;
      b.beginPath();
      // 起点 7.5px：与原 CSS `radial-gradient` 点阵（点画在每格中心）对齐
      for (let y = GAP / 2; y < H; y += GAP) {
        for (let x = GAP / 2; x < W; x += GAP) {
          b.moveTo(x + 1, y);
          b.arc(x, y, 1, 0, TAU);
          // 约 4% 的点会缓慢闪烁（周期 3—6s）
          S.dots.push({ x, y, tw: r() < 0.04 ? 0.5 + r() : 0, ph: r() * TAU });
        }
      }
      b.fill();
    }
    S.base = base;
  }
  const c = cv.getContext('2d');
  if (!c) return null;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
  c.drawImage(S.base!, 0, 0);
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  return c;
}

/** 量出四格里文字与环的实际墨迹区域。⚠ 用 Range 取行盒，而不是元素盒——元素盒是整格宽，会把整格都判成"文字区" */
function measure(now: number): void {
  const hero = S.hero;
  if (!hero) return;
  S.textAt = now;
  const hr = hero.getBoundingClientRect();
  const out: Box[] = [];
  const range = document.createRange();
  for (const el of Array.from(hero.querySelectorAll('.hstat-lab, .hstat-val, .hstat-fr, .hstat-ring'))) {
    range.selectNodeContents(el);
    const rects = el.classList.contains('hstat-ring') ? [el.getBoundingClientRect()] : Array.from(range.getClientRects());
    for (const r of rects) {
      if (r.width < 1 || r.height < 1) continue;
      out.push({ l: r.left - hr.left, t: r.top - hr.top, r: r.right - hr.left, b: r.bottom - hr.top });
    }
  }
  S.text = out;
}

/** 避让系数：文字区内 0.3，外扩 14px 内线性回到 1 */
function shade(x: number, y: number): number {
  let d = Infinity;
  for (const b of S.text) {
    const dx = Math.max(b.l - x, 0, x - b.r), dy = Math.max(b.t - y, 0, y - b.b);
    d = Math.min(d, Math.hypot(dx, dy));
    if (d === 0) break;
  }
  return 0.3 + 0.7 * clamp(d / 14, 0, 1);
}

function draw(now: number, still: boolean): void {
  const c = fit();
  if (!c) return;
  if (still || now - S.textAt > 1000) measure(now);
  const { W, H } = S;
  const t = (now - S.t0) / 1000;

  /* ① 漂移光晕：原 CSS 是 520px 圆、right:-90 / top:-220、14s alternate 往返位移 (-56, 34) + 放大 1.1 */
  {
    const k = still ? 0.5 : (1 - Math.cos((t / 14) * Math.PI)) / 2;
    const R = 260 * (1 + 0.1 * k);
    const cx = W + 90 - 260 - 56 * k, cy = -220 + 260 + 34 * k;
    const g = c.createRadialGradient(cx, cy, 0, cx, cy, R);
    const a = 0.3 * (0.6 + 0.4 * k);
    g.addColorStop(0, `rgba(138,141,184,${a})`);
    g.addColorStop(0.62, 'rgba(138,141,184,0)');
    c.fillStyle = g;
    c.fillRect(cx - R, cy - R, R * 2, R * 2);
  }

  /* ② 点阵高光：扫光 + 闪烁 + 指针照亮。底图已经画好，这里只叠"额外的亮度" */
  if (!still) {
    const band = 150;
    const k = (now % SWEEP_MS) / SWEEP_MS / 0.55; // 0—1 在掠过；>1 停在画外
    const span = W + H * 0.5 + band * 2;
    const sx = k <= 1 ? -band + span * easeInOut(k) : -1e9;
    const px = S.px, py = S.py;
    c.fillStyle = '#fff';
    for (const d of S.dots) {
      let a = 0;
      const s = d.x + d.y * 0.5 - sx; // 斜向（左下 → 右上）的带
      if (s > -band && s < band) { const q = 1 - Math.abs(s) / band; a += q * q * 0.16; }
      if (d.tw) a += (0.5 + 0.5 * Math.sin(t * TAU / (3 + d.tw * 2) + d.ph)) * 0.2;
      let r = 1;
      if (px !== null) {
        const dd = Math.hypot(d.x - px, d.y - py);
        if (dd < 120) { const q = 1 - dd / 120; a += q * q * 0.34; r += q * 0.5; }
      }
      if (a < 0.012) continue;
      c.globalAlpha = Math.min(0.55, a);
      c.beginPath();
      c.arc(d.x, d.y, r, 0, TAU);
      c.fill();
    }
    c.globalAlpha = 1;
  }

  /* ③ 星链：竖排（≤1250px）后右侧没有成片空区，不画 */
  if (!mq(NARROW)) drawNet(c, now, t, still);
}

const easeInOut = (k: number): number => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);

/** 分段画线：每 10px 一段，各段按中点的「往左淡出 × 避让文字」单独取透明度 */
function segLine(
  c: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number,
  a: number, fade: (x: number) => number,
): void {
  const n = Math.max(1, Math.ceil(Math.hypot(x2 - x1, y2 - y1) / 10));
  for (let i = 0; i < n; i++) {
    const ax = x1 + ((x2 - x1) * i) / n, ay = y1 + ((y2 - y1) * i) / n;
    const bx = x1 + ((x2 - x1) * (i + 1)) / n, by = y1 + ((y2 - y1) * (i + 1)) / n;
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    const k = a * fade(mx) * shade(mx, my);
    if (k < 0.004) continue;
    c.strokeStyle = `rgba(255,255,255,${k})`;
    c.beginPath(); c.moveTo(ax, ay); c.lineTo(bx, by); c.stroke();
  }
}

function drawNet(c: CanvasRenderingContext2D, now: number, t: number, still: boolean): void {
  const { W, H } = S;
  const x0 = W * 0.28, x1 = W - 26, y0 = 22, y1 = H - 22;
  /* 往左淡出：左栏是标题区、字最大，不该压装饰（原 SVG 的 mask 同一思路） */
  const fade = (x: number): number => clamp((x - x0) / (W * 0.2), 0, 1);
  const { nodes, edges } = NET;
  const amp = still ? 0 : 3.2;
  const pos = nodes.map((n) => ({
    x: x0 + n.u * (x1 - x0) + Math.sin(t * n.sp + n.ph) * amp,
    y: y0 + n.v * (y1 - y0) + Math.cos(t * n.sp * 0.8 + n.ph) * amp,
  }));
  /* 点亮开场：每个节点按 rank 依次亮起，连线在两端都亮了之后生长 */
  const it = S.intro0 < 0 ? Infinity : now - S.intro0;
  const lit = (n: Node): number => (it === Infinity ? 1 : easeOut((it - 120 - n.rank * 55) / 420));
  const litAt = (n: Node): number => 120 + n.rank * 55 + 300;
  const introDone = it > INTRO_MS + 600;
  if (introDone && S.intro0 >= 0) S.intro0 = -1;

  c.lineWidth = 1;
  for (const e of edges) {
    const A = nodes[e.a]!, B = nodes[e.b]!;
    const pa = pos[e.a]!, pb = pos[e.b]!;
    let k = 1;
    if (it !== Infinity) k = easeOut((it - Math.max(litAt(A), litAt(B))) / 450);
    if (k <= 0) continue;
    const ex = pa.x + (pb.x - pa.x) * k, ey = pa.y + (pb.y - pa.y) * k;
    segLine(c, pa.x, pa.y, ex, ey, 0.075, fade);
  }

  /* 指针连线：指针 150px 内的节点向指针拉一根细线 */
  const px = S.px, py = S.py;
  if (px !== null && !still) {
    for (let i = 0; i < nodes.length; i++) {
      const p = pos[i]!;
      const d = Math.hypot(p.x - px, p.y - py);
      if (d > 150) continue;
      segLine(c, p.x, p.y, px, py, (1 - d / 150) * 0.2, fade);
    }
  }

  /* 流光：开场结束后，每 1.4—2.4s 从随机一条连线出发一颗光点，到站后有一半概率接着走下一段 */
  if (!still && introDone) {
    if (now > S.nextPulse && S.pulses.length < 4) {
      const e = Math.floor(Math.random() * edges.length);
      S.pulses.push({ e, dir: Math.random() < 0.5 ? 1 : -1, t: now, len: 0, hops: 0 });
      S.nextPulse = now + 1400 + Math.random() * 1000;
    }
    const next: Pulse[] = [];
    for (const p of S.pulses) {
      const E = edges[p.e]!;
      const from = p.dir === 1 ? E.a : E.b, to = p.dir === 1 ? E.b : E.a;
      const pa = pos[from]!, pb = pos[to]!;
      const L = Math.hypot(pb.x - pa.x, pb.y - pa.y) || 1;
      const k = ((now - p.t) / 1000) * 70 / L; // 70px/s
      if (k >= 1) {
        S.flash.set(to, now);
        if (p.hops < 2 && Math.random() < 0.5) {
          const cand = edges.map((x, i) => ({ x, i })).filter(({ x, i }) => i !== p.e && (x.a === to || x.b === to));
          const pick = cand[Math.floor(Math.random() * cand.length)];
          if (pick) next.push({ e: pick.i, dir: pick.x.a === to ? 1 : -1, t: now, len: 0, hops: p.hops + 1 });
        }
        continue;
      }
      next.push(p);
      const hx = pa.x + (pb.x - pa.x) * k, hy = pa.y + (pb.y - pa.y) * k;
      const tail = Math.min(28, L * k);
      const tx = hx - ((pb.x - pa.x) / L) * tail, ty = hy - ((pb.y - pa.y) / L) * tail;
      const f = fade(hx) * shade(hx, hy);
      const g = c.createLinearGradient(tx, ty, hx, hy);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(1, `rgba(255,255,255,${0.42 * f})`);
      c.strokeStyle = g;
      c.lineWidth = 1.4;
      c.beginPath(); c.moveTo(tx, ty); c.lineTo(hx, hy); c.stroke();
      c.lineWidth = 1;
      c.fillStyle = `rgba(255,255,255,${0.7 * f})`;
      c.beginPath(); c.arc(hx, hy, 1.5, 0, TAU); c.fill();
    }
    S.pulses = next;
  }

  /* 节点：底亮度 + 开场点亮时的闪光 + 光点到站时的闪光 + 指针靠近时提亮 */
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!, p = pos[i]!;
    const k = lit(n);
    if (k <= 0) continue;
    const f = fade(p.x) * shade(p.x, p.y);
    let glow = 0;
    if (it !== Infinity) glow = Math.max(0, 1 - Math.abs(it - 120 - n.rank * 55 - 260) / 420);
    const fl = S.flash.get(i);
    if (fl !== undefined) {
      const q = 1 - (now - fl) / 900;
      if (q > 0) glow = Math.max(glow, q); else S.flash.delete(i);
    }
    if (px !== null) glow = Math.max(glow, (1 - Math.hypot(p.x - px, p.y - py) / 150) * 0.6);
    if (glow > 0) {
      const R = n.r * 5;
      const g = c.createRadialGradient(p.x, p.y, 0, p.x, p.y, R);
      g.addColorStop(0, `rgba(255,255,255,${0.22 * glow * f})`);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = g;
      c.fillRect(p.x - R, p.y - R, R * 2, R * 2);
    }
    const a = (n.hub ? 0.26 : 0.15) + glow * 0.3;
    c.fillStyle = `rgba(255,255,255,${Math.min(0.75, a) * f * k})`;
    c.beginPath(); c.arc(p.x, p.y, n.r * (0.4 + 0.6 * k), 0, TAU); c.fill();
  }
}

/**
 * 大数跳数：首次进页时把 `.hero .mo-num` 从 0 跳到真值（保留原小数位数）。
 * ⚠ 只改显示过程，**终值就是服务端算好写进 HTML 的那个字符串**——最后一帧原样写回，不经过 JS 再算一遍（红线 4）。
 * ⚠ 「—」这类非数字（首期没有已结束周期）原样不动。
 * ⚠ 节奏对齐四枚环的 `ovRing`：延迟 340ms、时长 1.1s、缓出。
 */
export function countUp(root: HTMLElement | null): void {
  if (!root || mq(REDUCE)) return;
  const els = Array.from(root.querySelectorAll<HTMLElement>('.mo-num'));
  for (const el of els) {
    const raw = el.textContent?.trim() ?? '';
    if (!/^\d+(\.\d+)?$/.test(raw)) continue;
    const end = Number(raw);
    const dec = raw.includes('.') ? raw.split('.')[1]!.length : 0;
    if (end === 0) continue;
    el.textContent = (0).toFixed(dec);
    const t0 = performance.now() + 340;
    const step = (now: number): void => {
      if (!el.isConnected) return;
      const k = (now - t0) / 1100;
      if (k >= 1) { el.textContent = raw; return; }
      el.textContent = (end * easeOut(k)).toFixed(dec);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}
